import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { createPrintAgent } from '../lib/printAgent.js';
import LabelPreview from '../components/LabelPreview.jsx';
import { isBarcodeObject } from '../lib/barcodes.js';
import { isStandardFont } from '../studio/zebra/ZebraFonts.js';
import { getDownloadedFont } from '../studio/zebra/DownloadedFonts.js';

const DEBUG_SERVER_URL = 'http://127.0.0.1:7777/event';
const DEBUG_SESSION_ID = 'print-websocket-cursor';
const RECONNECT_INTERVAL_MS = 5000;
const outputOptions = [
  ['PRINTER', 'Enviar para impressora'],
  ['FILE', 'Salvar arquivo do trabalho'],
  ['BROWSER', 'Abrir impressão do navegador'],
];

function reportPrintDebug(hypothesisId, msg, data = {}) {
  fetch(DEBUG_SERVER_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sessionId: DEBUG_SESSION_ID,
      runId: 'pre-fix',
      hypothesisId,
      location: 'frontend/src/pages/Print.jsx',
      msg: `[DEBUG] ${msg}`,
      data,
      ts: Date.now(),
    }),
  }).catch(() => { });
}

function fieldLabel(object) {
  return object.label || object.field;
}

function resolveDownloadedFonts(content) {
  const downloadedFonts = {};
  const missing = [];
  for (const object of content?.objects || []) {
    const font = object.font;
    if (!font) continue;
    const name = typeof font === 'string' ? font : font.name;
    if (!name || isStandardFont(name)) continue;
    const registered = getDownloadedFont(name);
    if (registered) downloadedFonts[name] = registered;
    else missing.push(name);
  }
  return { downloadedFonts, missing };
}

const HIDDEN_FIELDS = new Set([
  'FEITO NO BRASIL',
  '14.462.060/0001-30',
  'CNPJ',
  '8% ELASTANO',
  '46% POLIESTER',
  '46% ALGODíO',
  'POLO WEST LTDA',
  'CONFECÇíO',
  'CAMISARIA E',
]);

function downloadJson(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

let errorAudioCtx = null;

// ponytail: cria/resume o AudioContext no clique (gesto do usuário) para o beep tocar após o await.
function primeErrorAudio() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!errorAudioCtx) errorAudioCtx = new Ctx();
    if (errorAudioCtx.state === 'suspended') errorAudioCtx.resume();
  } catch {
    /* som indisponível */
  }
}

function playErrorBeep() {
  try {
    primeErrorAudio();
    const ctx = errorAudioCtx;
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(440, ctx.currentTime + 0.2);
    gain.gain.setValueAtTime(0.2, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.5);
  } catch {
    /* som indisponível */
  }
}

export default function Print() {
  const location = useLocation();
  const navigate = useNavigate();
  const selectedTemplateFromUrl = useMemo(() => new URLSearchParams(location.search).get('template') || '', [location.search]);
  const [templates, setTemplates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedId, setSelectedId] = useState(selectedTemplateFromUrl);
  const [template, setTemplate] = useState(null);
  const [values, setValues] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [copies, setCopies] = useState(1);
  const [pauseBetweenBatches, setPauseBetweenBatches] = useState(false);
  const [outputMode, setOutputMode] = useState('PRINTER');
  const [dataSourceType, setDataSourceType] = useState('MANUAL');
  const [sequenceMode, setSequenceMode] = useState('APPLICATION');
  const [sequenceConfig, setSequenceConfig] = useState('{\n  "start": 1,\n  "end": 100,\n  "step": 1,\n  "prefix": ""\n}');
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [agentOnline, setAgentOnline] = useState(false);
  const [agentConnecting, setAgentConnecting] = useState(false);
  const [windowsPrinters, setWindowsPrinters] = useState([]);
  const [selectedWindowsPrinter, setSelectedWindowsPrinter] = useState('');
  const [reprocessing, setReprocessing] = useState(false);
  const agentRef = useRef(null);
  const autoConnectAttemptedRef = useRef(false);
  const lastCursorDebugRef = useRef('');

  useEffect(() => {
    const agent = createPrintAgent(undefined, { autoReconnect: true });
    agentRef.current = agent;
    const unsubscribe = agent.onChange(setAgentOnline);
    return () => {
      unsubscribe();
      agent.disconnect();
    };
  }, []);

  async function loadInstalledPrinters() {
    try {
      const result = await agentRef.current.listInstalled();
      setWindowsPrinters(result.windows || []);
      if (!selectedWindowsPrinter && (result.windows || []).length > 0) setSelectedWindowsPrinter(result.windows[0].name);
    } catch (e) {
      /* offline — o indicador já informa */
    }
  }

  useEffect(() => {
    // #region debug-point B:print-state-change
    reportPrintDebug('B', 'print page agent state changed', { agentOnline, agentConnecting });
    // #endregion
    setAgentConnecting(false);
    if (agentOnline) {
      loadInstalledPrinters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentOnline]);

  async function ensureAgentConnection({ silent = false } = {}) {
    if (!agentRef.current) return false;
    if (agentRef.current.isOnline()) return true;

    // #region debug-point B:ensure-agent-connection
    reportPrintDebug('B', 'ensureAgentConnection called', { silent, agentOnline, agentConnecting });
    // #endregion
    setAgentConnecting(true);
    if (!silent) {
      setError('');
      setStatus('');
    }

    const connected = await agentRef.current.connect();
    setAgentConnecting(false);

    if (!connected && !silent) {
      setError('Nao foi possivel conectar ao Print Agent. Abra o agent local e tente novamente.');
    }

    return connected;
  }

  useEffect(() => {
    if (autoConnectAttemptedRef.current) return;
    autoConnectAttemptedRef.current = true;
    // #region debug-point B:auto-connect-attempt
    reportPrintDebug('B', 'auto connect attempt on print page mount');
    // #endregion
    void ensureAgentConnection({ silent: true });
  }, []);

  useEffect(() => {
    if (agentOnline || agentConnecting) return undefined;
    const timer = setInterval(() => {
      if (agentRef.current && !agentRef.current.isOnline()) {
        void ensureAgentConnection({ silent: true });
      }
    }, RECONNECT_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [agentOnline, agentConnecting]);

  function handleAgentStatusPointerMove(event) {
    const cursor = window.getComputedStyle(event.currentTarget).cursor;
    if (lastCursorDebugRef.current === cursor) return;
    lastCursorDebugRef.current = cursor;
    // #region debug-point C:cursor-change
    reportPrintDebug('C', 'agent status area cursor changed', {
      cursor,
      targetTag: event.target?.tagName ?? null,
      currentTargetTag: event.currentTarget?.tagName ?? null,
      agentOnline,
      agentConnecting,
    });
    // #endregion
  }

  function handleAgentStatusPointerEnter(event) {
    const cursor = window.getComputedStyle(event.currentTarget).cursor;
    lastCursorDebugRef.current = cursor;
    // #region debug-point C:cursor-enter
    reportPrintDebug('C', 'pointer entered agent status area', {
      cursor,
      targetTag: event.target?.tagName ?? null,
      agentOnline,
      agentConnecting,
    });
    // #endregion
  }

  async function loadBase() {
    try {
      const [publishedTemplates, printJobs] = await Promise.all([
        api.listTemplates({ status: 'PUBLISHED' }),
        api.listPrintJobs(),
      ]);
      setTemplates(publishedTemplates);
      setJobs(printJobs);
    } catch (e) {
      setError(e.message);
    }
  }

  useEffect(() => {
    loadBase();
  }, []);

  useEffect(() => {
    setSelectedId(selectedTemplateFromUrl);
  }, [selectedTemplateFromUrl]);

  useEffect(() => {
    if (!selectedId) {
      setTemplate(null);
      return;
    }
    api
      .getTemplate(selectedId)
      .then((result) => {
        setTemplate(result);
        if (result.printer?.name) {
          setSelectedWindowsPrinter(result.printer.name);
        }
        const initialValues = {};
        for (const object of result.content?.objects || []) {
          if (object.type === 'variable' || (object.field && isBarcodeObject(object))) {
            initialValues[object.field] = '';
          }
        }
        setValues(initialValues);
      })
      .catch((e) => setError(e.message));
  }, [selectedId]);

  function handleTemplateChange(nextTemplateId) {
    setSelectedId(nextTemplateId);

    const searchParams = new URLSearchParams(location.search);
    if (nextTemplateId) {
      searchParams.set('template', nextTemplateId);
    } else {
      searchParams.delete('template');
    }

    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString() ? `?${searchParams.toString()}` : '',
      },
      { replace: true },
    );
  }

  const fields = useMemo(() => {
    if (!template?.content) return [];
    return template.content.objects
      .filter((object) => object.type === 'variable' || (object.field && isBarcodeObject(object)))
      .filter((object) => !HIDDEN_FIELDS.has(fieldLabel(object)) && !HIDDEN_FIELDS.has(object.placeholder) && !HIDDEN_FIELDS.has(object.field))
      .map((object) => ({ field: object.field, label: fieldLabel(object), placeholder: object.placeholder }));
  }, [template]);

  function destinationName() {
    if (outputMode === 'FILE') return 'Arquivo';
    if (outputMode === 'BROWSER') return 'Navegador';
    return selectedWindowsPrinter;
  }

  function communicationType() {
    if (outputMode === 'FILE') return 'FILE';
    return 'WINDOWS_PRINTER';
  }

  function buildJobPayload() {
    const parsedSequence = JSON.parse(sequenceConfig || '{}');
    return {
      name: `impressão ${template.name}`,
      templateId: template.id,
      labelFormatId: template.labelFormatId || undefined,
      printerName: destinationName(),
      communicationType: communicationType(),
      outputMode,
      quantity,
      copies,
      pauseBetweenBatches,
      sequenceMode,
      dataSourceType,
      payload: {
        values: {},
        templateName: template.name,
        templateContent: template.content,
        labelFormat: template.labelFormat,
      },
      sequenceConfig: parsedSequence,
      fileName: outputMode === 'FILE' ? `${template.name.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}.print-job.json` : undefined,
    };
  }

  async function createJob() {
    if (!template) return;
    setError('');
    setStatus('');
    primeErrorAudio();

    // quantidade = total de etiquetas; cada coluna é uma etiqueta
    const columns = template.labelFormat?.columns || 1;
    const totalPrints = Math.max(1, Math.ceil((quantity * copies) / columns));

    if (outputMode === 'PRINTER') {
      try {
        const connected = await ensureAgentConnection({ silent: true });
        if (!connected) {
          setError('Print Agent nao encontrado. Abra o agent local e tente novamente.');
          playErrorBeep();
          return;
        }
        if (!selectedWindowsPrinter) {
          setError('Selecione uma impressora.');
          playErrorBeep();
          return;
        }
        const { downloadedFonts, missing } = resolveDownloadedFonts(template.content);
        if (missing.length) {
          setError(`Fonte(s) baixada(s) não registrada(s) na impressora: ${missing.join(', ')}. Registre antes de imprimir.`);
          playErrorBeep();
          return;
        }
        const { zpl } = await api.generateCode({
          templateId: template.id,
          values: {},
          content: template.content,
          labelFormat: template.labelFormat,
          language: 'ZPL',
          downloadedFonts,
        });
        const job = await api.createPrintJob(buildJobPayload());
        const agentJob = { jobId: job.id, label: template.name, transport: 'windows', printerName: selectedWindowsPrinter, code: zpl, copies: totalPrints };
        try {
          const result = await agentRef.current.sendJob(agentJob);
          await api.updatePrintJob(job.id, { status: 'COMPLETED' });
          setStatus(`impressão concluída via Print Agent (${result.bytesSent} bytes).`);
        } catch (e) {
          await api.updatePrintJob(job.id, { status: 'FAILED' }).catch(() => { });
          setError(e.message);
          playErrorBeep();
        }
        await loadBase();
      } catch (e) {
        setError(e.message);
        playErrorBeep();
      }
      return;
    }

    try {
      const payload = buildJobPayload();
      const job = await api.createPrintJob(payload);
      if (outputMode === 'FILE') {
        downloadJson(job.fileName || 'print-job.json', payload);
      } else if (outputMode === 'BROWSER') {
        setTimeout(() => window.print(), 150);
      }
      setStatus(`Trabalho "${job.name}" registrado com status ${job.status}.`);
      await loadBase();
    } catch (e) {
      setError(e.message);
    }
  }

  async function reprocessJob(id) {
    setError('');
    setStatus('');
    primeErrorAudio();
    try {
      const connected = await ensureAgentConnection({ silent: true });
      if (!connected) {
        setError('Print Agent nao encontrado. Abra o agent local e tente novamente.');
        playErrorBeep();
        return;
      }
      const job = await api.getPrintJob(id);
      if (job.status !== 'QUEUED' && job.status !== 'FAILED') {
        setError('Somente trabalhos pendentes podem ser reprocessados.');
        playErrorBeep();
        return;
      }
      const { downloadedFonts, missing } = resolveDownloadedFonts(job.payload?.templateContent);
      if (missing.length) {
        setError(`Fonte(s) baixada(s) não registrada(s) na impressora: ${missing.join(', ')}. Registre antes de imprimir.`);
        playErrorBeep();
        return;
      }
      const { zpl } = await api.generateCode({
        templateId: job.template?.id,
        values: {},
        content: job.payload?.templateContent,
        labelFormat: job.payload?.labelFormat,
        language: 'ZPL',
        downloadedFonts,
      });
      const columns = job.payload?.labelFormat?.columns || 1;
      const totalPrints = Math.max(1, Math.ceil(((job.quantity || 1) * (job.copies || 1)) / columns));
      if (!windowsPrinters.some((p) => p.name === job.printerName)) {
        setError(`Impressora "${job.printerName || '—'}" não encontrada no Print Agent.`);
        playErrorBeep();
        return;
      }
      const agentJob = { jobId: id, label: job.template?.name, transport: 'windows', printerName: job.printerName, code: zpl, copies: totalPrints };

      await api.updatePrintJob(id, { status: 'PRINTING' });
      try {
        const result = await agentRef.current.sendJob(agentJob);
        await api.updatePrintJob(id, { status: 'COMPLETED' });
        setStatus(`Trabalho reprocessado e concluído (${result.bytesSent} bytes).`);
      } catch (e) {
        await api.updatePrintJob(id, { status: 'FAILED' }).catch(() => { });
        setError(e.message);
        playErrorBeep();
      }
    } catch (e) {
      setError(e.message);
      playErrorBeep();
    }
    await loadBase();
  }

  async function reprocessQueued() {
    if (reprocessing) return;
    setReprocessing(true);
    setError('');
    setStatus('');
    primeErrorAudio();
    try {
      const pending = jobs.filter((job) => job.status === 'QUEUED');
      for (const job of pending) {
        await reprocessJob(job.id);
      }
      await loadBase();
    } finally {
      setReprocessing(false);
    }
  }

  async function deleteJob(id) {
    setError('');
    try {
      await api.deletePrintJob(id);
      agentRef.current.deleteJob(id);
      await loadBase();
    } catch (e) {
      setError(e.message);
    }
  }

  async function clearJobs() {
    if (!window.confirm('Excluir todos os trabalhos da fila?')) return;
    setError('');
    try {
      await api.clearPrintJobs();
      agentRef.current.clearJobs();
      await loadBase();
    } catch (e) {
      setError(e.message);
    }
  }

  return (
    <>
      <style>{'@media print { body * { visibility: hidden; } #print-area, #print-area * { visibility: visible; } #print-area { position: absolute; left: 0; top: 0; } }'}</style>

      <div className="space-y-6">
        <div>
          <h1 className="studio-uniform-title font-h2 text-h2 tracking-[-0.04em] text-on-surface">Gestão de impressão</h1>
          <div
            className="mt-3 flex items-center gap-2"
            onPointerEnter={handleAgentStatusPointerEnter}
            onPointerMove={handleAgentStatusPointerMove}
          >
            <span className={`h-2 w-2 rounded-full ${agentOnline ? 'bg-green-500' : 'bg-red-500'}`} />
            <span className={`inline-flex min-w-[132px] text-[10px] font-semibold uppercase tracking-[0.16em] ${agentOnline ? 'text-on-surface-variant' : 'text-red-500'}`}>
              Print Agent {agentOnline ? 'online' : 'offline'}
            </span>
            <button
              type="button"
              onClick={() => {
                if (agentOnline || agentConnecting) return;
                void ensureAgentConnection();
              }}
              aria-disabled={agentOnline || agentConnecting}
              className={`ml-1 inline-flex min-w-[124px] items-center justify-center rounded-full border border-primary/40 px-3 py-1 text-[10px] font-semibold text-primary transition-colors ${agentOnline || agentConnecting ? 'opacity-50 cursor-pointer' : 'hover:bg-primary/10 cursor-pointer'
                }`}
            >
              {agentOnline ? 'Conectado' : agentConnecting ? 'Conectando...' : 'Conectar agent'}
            </button>
            <a href="etiquetandapp://parear=studio" className="rounded-full border border-primary/40 px-3 py-1 text-[10px] font-semibold text-primary transition-colors hover:bg-primary/10">
              Abrir Print Agent
            </a>
            <Link to="/baixar-app" className="ml-1 rounded-full border border-surface-container-highest bg-surface-container-low px-3 py-1 text-[10px] font-semibold text-on-surface-variant transition-colors hover:text-on-surface">
              Baixar App
            </Link>
          </div>
        </div>

        {error && <div className="rounded-2xl border border-error/20 bg-error-container/40 px-4 py-3 text-[10px] text-error">{error}</div>}
        {status && <div className="rounded-2xl border border-primary/10 bg-primary-container/40 px-4 py-3 text-[10px] text-on-primary-container">{status}</div>}

        <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
          <div className="rounded-[28px] border border-surface-container-highest bg-surface px-6 py-6 shadow-[0_14px_28px_rgba(15,23,42,0.05)]">
            <h2 className="studio-uniform-title font-h3 text-h3 tracking-[-0.03em] text-on-surface">Novo trabalho</h2>
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <label className="flex flex-col gap-2 md:col-span-2">
                <span className="text-[10px] font-semibold text-on-surface-variant">Etiqueta publicada</span>
                <select
                  value={selectedId}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
                >
                  <option value="">Selecione uma etiqueta…</option>
                  {templates.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {selectedId && (
              <>
                <div className="mt-5 grid gap-4 md:grid-cols-2">
                  <label className="flex flex-col gap-2">
                    <span className="text-[10px] font-semibold text-on-surface-variant">Saída</span>
                    <select value={outputMode} onChange={(e) => setOutputMode(e.target.value)} className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15">
                      {outputOptions.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </label>

                  {outputMode === 'PRINTER' && (
                    <>
                      <label className="flex flex-col gap-2">
                        <span className="text-[10px] font-semibold text-on-surface-variant">Transporte</span>
                        <select value={selectedWindowsPrinter} onChange={(e) => setSelectedWindowsPrinter(e.target.value)} className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15">
                          {windowsPrinters.length === 0 && <option value="">Nenhuma impressora instalada encontrada</option>}
                          {windowsPrinters.map((p) => (
                            <option key={p.name} value={p.name}>{p.name} ({p.port})</option>
                          ))}
                        </select>
                      </label>

                      {windowsPrinters.length === 0 && (
                        <p className="text-[10px] text-on-surface-variant">Nenhuma impressora instalada encontrada.</p>
                      )}
                    </>
                  )}

                  <label className="flex flex-col gap-2">
                    <span className="text-[10px] font-semibold text-on-surface-variant">Quantidade</span>
                    <input type="number" min="1" value={quantity} onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))} className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15" />
                  </label>

                  <label className="flex flex-col gap-2">
                    <span className="text-[10px] font-semibold text-on-surface-variant">Cópias</span>
                    <input type="number" min="1" value={copies} onChange={(e) => setCopies(Math.max(1, Number(e.target.value)))} className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15" />
                  </label>

                  <label className="flex items-center gap-3 rounded-2xl border border-surface-container-high px-4 py-3 text-[10px] text-on-surface">
                    <input type="checkbox" checked={pauseBetweenBatches} onChange={(e) => setPauseBetweenBatches(e.target.checked)} />
                    Pausa entre lotes
                  </label>
                </div>


                {/* {template && (
              <>
                <div className="mt-5 space-y-3">
                  {fields.length === 0 && <p className="text-[10px] text-on-surface-variant">Etiqueta sem campos variáveis.</p>}
                  {fields.map((field) => (
                    <label key={field.field} className="flex flex-col gap-2">
                      <span className="text-[10px] font-semibold text-on-surface-variant">{field.label}</span>
                      <input
                        value={values[field.field] || ''}
                        onChange={(e) => setValues({ ...values, [field.field]: e.target.value })}
                        placeholder={field.placeholder || field.field}
                        className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
                      />
                    </label>
                  ))}
                </div>
              </>
            )} */}

                <div className="mt-5">
                  <button type="button" onClick={createJob} disabled={!template} className="rounded-2xl bg-primary px-6 py-3 text-[11px] font-semibold text-on-primary shadow-[0_12px_24px_rgba(249,115,22,0.2)] disabled:opacity-50">
                    Enviar para impressão
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="space-y-6">
            <div className="rounded-[28px] border border-surface-container-highest bg-surface px-6 py-6 shadow-[0_14px_28px_rgba(15,23,42,0.05)]">
              <h2 className="studio-uniform-title font-h3 text-h3 tracking-[-0.03em] text-on-surface">Pré-visualização</h2>
              {template && (
                <div className="mt-4 rounded-[24px] bg-surface-container-low px-4 py-4">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Formato aplicado</div>
                  <div className="mt-2 text-[11px] font-semibold text-on-surface">{template.labelFormat?.name || 'Sem formato vinculado'}</div>
                  <div className="mt-1 text-[10px] text-on-surface-variant">
                    {template.labelFormat
                      ? `${template.labelFormat.widthMm} x ${template.labelFormat.heightMm} mm • ${template.labelFormat.columns} coluna(s)`
                      : 'Vincule um formato para usar as dimensões da mídia.'}
                  </div>
                  {template.printer && (
                    <div className="mt-1 text-[10px] text-on-surface-variant">
                      Impressora: {template.printer.name} • {template.printer.dpi} DPI
                    </div>
                  )}
                </div>
              )}
              <div id="print-area" className="mt-4 rounded-[24px] border border-surface-container-high bg-white p-5">
                {template ? <LabelPreview content={template.content} values={values} scale={1.1} columns={template.labelFormat?.columns || 1} gapMm={template.labelFormat?.horizontalGapMm || 0} dpi={template.printer?.dpi || 203} /> : <p className="text-[10px] text-on-surface-variant">Selecione uma etiqueta publicada para visualizar.</p>}
              </div>
            </div>

            <div className="rounded-[28px] border border-surface-container-highest bg-surface px-6 py-6 shadow-[0_14px_28px_rgba(15,23,42,0.05)]">
              <div className="flex items-center justify-between gap-3">
                <h2 className="studio-uniform-title font-h3 text-h3 tracking-[-0.03em] text-on-surface">Fila registrada</h2>
                <div className="flex items-center gap-2">
                  {jobs.some((job) => job.status === 'QUEUED') && (
                    <button type="button" onClick={reprocessQueued} disabled={reprocessing} className="rounded-full border border-primary/40 px-3 py-1 text-[10px] font-semibold text-primary transition-colors hover:bg-primary/10 disabled:opacity-50">
                      {reprocessing ? 'Reprocessando…' : 'Reprocessar pendentes'}
                    </button>
                  )}
                  {jobs.length > 0 && (
                    <button type="button" onClick={clearJobs} className="rounded-full border border-error/40 px-3 py-1 text-[10px] font-semibold text-error transition-colors hover:bg-error/10">
                      Limpar lista
                    </button>
                  )}
                  <span className="rounded-full bg-primary-container px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-on-primary-container">
                    {jobs.length} trabalhos
                  </span>
                </div>
              </div>
              <div className="mt-4 space-y-3">
                {jobs.length === 0 ? (
                  <p className="text-[10px] text-on-surface-variant">Nenhum trabalho registrado ainda.</p>
                ) : (
                  jobs.map((job) => (
                    <div key={job.id} className="rounded-[22px] border border-surface-container-high bg-surface-container-low px-4 py-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-[11px] font-semibold text-on-surface">{job.name}</div>
                          <div className="mt-1 text-[10px] text-on-surface-variant">
                            {job.template?.name || 'Sem etiqueta'} • {job.printerName || 'Sem impressora'}
                          </div>
                        </div>
                        <span className="rounded-full bg-surface px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">
                          {job.status}
                        </span>
                      </div>
                      <div className="mt-2 text-[10px] text-on-surface-variant">
                        {job.quantity} etiqueta(s) • {job.copies} cópia(s) • {job.outputMode}
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        {(job.status === 'QUEUED' || job.status === 'FAILED') && (
                          <button type="button" onClick={() => reprocessJob(job.id)} disabled={reprocessing} className="rounded-full border border-surface-container-high bg-surface px-3 py-1 text-[10px] font-semibold text-on-surface-variant transition-colors hover:text-on-surface disabled:opacity-50">
                            Reprocessar
                          </button>
                        )}
                        <button type="button" onClick={() => deleteJob(job.id)} className="rounded-full border border-surface-container-high bg-surface px-3 py-1 text-[10px] font-semibold text-on-surface-variant transition-colors hover:text-error">
                          Excluir
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
